import { z } from 'zod';
import { ANIMAL_SOURCES, ANIMAL_STATUSES, SEXES } from '../enums';
import { clientCreatedAt, isoDate, optText, santim, uuid, type SyncFields } from './common';

export const BREED_SUGGESTIONS = ['Holstein × local', 'Jersey × local', 'Holstein', 'Jersey', 'Local'] as const;

/** AR-1 + AR-4 + AR-8. Client fields of an animal. */
export const animalSchema = z
  .object({
    tag: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9-]{1,20}$/, 'Tag: 1–20 letters, digits or "-"'),
    name: optText(40),
    sex: z.enum(SEXES),
    breed: z.string().trim().min(1).max(60),
    birthDate: isoDate,
    birthDateEstimated: z.boolean().default(false),
    damId: uuid.nullish(),
    sireCode: optText(40),
    source: z.enum(ANIMAL_SOURCES),
    purchaseDate: isoDate.nullish(),
    purchasePriceSantim: santim(0).nullish(),
    openingParity: z.number().int().min(0).max(15).default(0),
    status: z.enum(ANIMAL_STATUSES).default('ACTIVE'),
    exitDate: isoDate.nullish(),
    exitNote: optText(500),
    photoFileId: uuid.nullish(),
    notes: optText(500),
    clientCreatedAt,
  })
  .superRefine((a, ctx) => {
    if (a.source === 'PURCHASED') {
      if (!a.purchaseDate) ctx.addIssue({ code: 'custom', path: ['purchaseDate'], message: 'Required' });
      else if (a.purchaseDate < a.birthDate)
        ctx.addIssue({ code: 'custom', path: ['purchaseDate'], message: 'Before birth date' });
      if (a.purchasePriceSantim === null || a.purchasePriceSantim === undefined)
        ctx.addIssue({ code: 'custom', path: ['purchasePriceSantim'], message: 'Required' });
    }
    if (a.status === 'ACTIVE') {
      if (a.exitDate) ctx.addIssue({ code: 'custom', path: ['exitDate'], message: 'Active animals have no exit' });
    } else {
      if (!a.exitDate) ctx.addIssue({ code: 'custom', path: ['exitDate'], message: 'Required' });
      else if (a.exitDate < a.birthDate)
        ctx.addIssue({ code: 'custom', path: ['exitDate'], message: 'Before birth date' });
      if (!a.exitNote) ctx.addIssue({ code: 'custom', path: ['exitNote'], message: 'Required' });
    }
    if (a.sex === 'MALE' && a.openingParity > 0)
      ctx.addIssue({ code: 'custom', path: ['openingParity'], message: 'Males cannot calve' });
    if (a.source === 'BORN_ON_FARM' && !a.damId)
      ctx.addIssue({ code: 'custom', path: ['damId'], message: 'Required for calves born on the farm' });
  });

export type AnimalData = z.output<typeof animalSchema>;
export type AnimalRecord = AnimalData & SyncFields;

/** Fields a MANAGER may change on an animal they did not create: the DEAD exit (AR-4). */
export const ANIMAL_EXIT_FIELDS = ['status', 'exitDate', 'exitNote'] as const;
